import React, { useEffect } from 'react';
import type { PropsWithChildren } from 'react';

import type { ReactSerializer, ReactSerializerInit } from '@atlaskit/renderer';

import { getEstimatedHeight } from './NodeEstimatedHeights';
import type { ChildInfoOutput } from './NodeEstimatedHeights';
import { NullRenderer } from './NullRenderer';
import { VirtualizedReactSerializer } from './VirtualizedReactSerializer';

// 99% of users have a window height of 2160px or less.
// Confluence has 300px of content before page content starts.
// Therefore, we set the minimum rendered height to 1860px.
const MINIMUM_RENDERED_HEIGHT = 1860;

export class AboveTheFoldReactSerializer extends VirtualizedReactSerializer {
	private onlySerializeFirstBatch: boolean;
	private onBatchRendered?: (done: boolean) => void;
	private estimatedHeight: number;

	// This stack is used to keep track of the estimated heights of child nodes as they are recursively
	// serialized depth first. The last index is the top of the stack.
	private childInfoOutputStack: ChildInfoOutput[];

	constructor(
		init: ReactSerializerInit,
		onlySerializeFirstBatch: boolean = false,
		onBatchRendered?: (done: boolean) => void,
	) {
		super(init);

		this.onlySerializeFirstBatch = onlySerializeFirstBatch;
		this.onBatchRendered = onBatchRendered;
		this.estimatedHeight = 0;
		this.childInfoOutputStack = [];
	}

	/**
	 * Like {@link ReactSerializer.resetState}. Named differently because a class cannot have a separate declaration of a private property of its base class.
	 */
	private resetProgressiveState() {
		this.estimatedHeight = 0;
		this.childInfoOutputStack = [];
	}

	/**
	 * @override
	 */
	serializeFragment(
		_fragment: SerializeFragmentParameters[0],
		_props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment
		if (isRoot) {
			this.resetProgressiveState();
		}

		const shouldSkipNodeSerialization =
			this.onlySerializeFirstBatch &&
			// Skip serialization of anything estimated to be below the fold.
			// Please note that a statically defined minimum rendered height is used
			// in part because the actual window height is not known during SSR. We must
			// render the same content during SSR and the initial render on the client in
			// order to avoid hydration issues.
			this.estimatedHeight > MINIMUM_RENDERED_HEIGHT;

		if (shouldSkipNodeSerialization) {
			// Returning a component, as opposed to null, is important to avoid breaking existing logic
			// within ADF node components that assume their children are React elements. NullRenderer
			// renders nothing, but is still a valid React element.
			return <NullRenderer />;
		}

		if (this.onlySerializeFirstBatch) {
			this.preSerializeFragment();
		}
		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);
		if (this.onlySerializeFirstBatch) {
			this.postSerializeFragment(parentInfo);
		}

		if (isRoot) {
			return (
				<OnBatchRenderedComponent
					onBatchRendered={this.onBatchRendered}
					onlySerializeFirstBatch={this.onlySerializeFirstBatch}
				>
					{nodeElement}
				</OnBatchRenderedComponent>
			);
		} else {
			return nodeElement;
		}
	}

	private preSerializeFragment = () => {
		// Adds a new object onto childInfoOutputStack for this node. This allows children to report their
		// estimated heights when they are serialized.
		this.childInfoOutputStack.push({ estimatedChildHeights: [] });
	};

	private postSerializeFragment = (parentInfo: SerializeFragmentParameters[4]) => {
		// Removes the new object from childInfoOutputStack for this node.
		const thisNodesChildInfoOutput = this.childInfoOutputStack.pop() || {
			estimatedChildHeights: [],
		};

		// Get the estimated height of this node based on its type and the heights of its children.
		const thisNode = parentInfo?.path?.[parentInfo.path.length - 1];
		const thisNodeEstimatedHeight = thisNode
			? getEstimatedHeight(thisNode, thisNodesChildInfoOutput)
			: 0;

		// Reports the estimated height of this node to its parent, if it has one.
		if (this.childInfoOutputStack.length > 0) {
			this.childInfoOutputStack[this.childInfoOutputStack.length - 1].estimatedChildHeights.push(
				thisNodeEstimatedHeight,
			);
		}

		// For top-level nodes, we add the estimated height to the total estimated height.
		// This is used to determine if the node is above or below the fold and whether
		// it should be serialized.
		if (parentInfo?.path?.length === 1) {
			this.estimatedHeight += thisNodeEstimatedHeight || 0;
		}
	};
}

type OnBatchRenderedComponentProps = PropsWithChildren<{
	onBatchRendered: ((done: boolean) => void) | undefined;
	onlySerializeFirstBatch: boolean;
}>;

const OnBatchRenderedComponent = ({
	children,
	onBatchRendered,
	onlySerializeFirstBatch,
}: OnBatchRenderedComponentProps) => {
	useEffect(() => {
		// eslint-disable-next-line compat/compat
		const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
		const cancelHandle = requestIdleCallback(() => {
			onBatchRendered?.(!onlySerializeFirstBatch);
		});

		return () => {
			const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
			cancelIdleCallback(cancelHandle);
		};
	}, [onBatchRendered, onlySerializeFirstBatch]);

	return children;
};

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;
